import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Text,
  StyleSheet,
} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import StudentNotesListItem from './StudentNotesListItem';

const ROW_HEIGHT = 52;
const VISIBLE_ROWS = 3;
const VIEWPORT_HEIGHT = ROW_HEIGHT * VISIBLE_ROWS;

/**
 * Scrollable note list (~3 rows visible) with up/down affordances.
 */
export default function StudentNotesScrollableList({
  notes,
  loading,
  emptyLabel,
  activeId,
  onPressNote,
  onDeletePress,
  onMenuPress,
}) {
  const theme = useTheme();
  const gold = theme.colors.primary || '#d4af37';
  const scrollRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(VIEWPORT_HEIGHT);

  const canScroll = contentHeight > viewportHeight + 4;
  const atTop = scrollY <= 4;
  const atBottom = scrollY + viewportHeight >= contentHeight - 4;

  const scrollBy = useCallback(
    (delta) => {
      const next = Math.max(0, Math.min(scrollY + delta, Math.max(0, contentHeight - viewportHeight)));
      scrollRef.current?.scrollTo({ y: next, animated: true });
      setScrollY(next);
    },
    [scrollY, contentHeight, viewportHeight]
  );

  useEffect(() => {
    setScrollY(0);
    scrollRef.current?.scrollTo({ y: 0, animated: false });
  }, [notes.length, loading]);

  if (loading) {
    return (
      <ActivityIndicator color={gold} style={styles.loader} />
    );
  }

  if (!notes.length) {
    return <Text style={[styles.empty, { color: theme.isDarkMode ? '#888' : '#666' }]}>{emptyLabel}</Text>;
  }

  return (
    <View style={styles.wrap}>
      <TouchableOpacity
        style={[styles.arrowBtn, !canScroll && styles.arrowDisabled]}
        onPress={() => scrollBy(-ROW_HEIGHT)}
        disabled={!canScroll || atTop}
        accessibilityLabel="Scroll notes up"
      >
        <MaterialIcons name="keyboard-arrow-up" size={28} color={canScroll && !atTop ? gold : '#666'} />
      </TouchableOpacity>

      <View
        style={[styles.viewport, { height: VIEWPORT_HEIGHT, borderColor: theme.isDarkMode ? '#444' : '#DDD' }]}
        onLayout={(e) => setViewportHeight(e.nativeEvent.layout.height)}
      >
        <ScrollView
          ref={scrollRef}
          style={styles.scroll}
          nestedScrollEnabled
          showsVerticalScrollIndicator={canScroll}
          scrollEnabled={canScroll}
          onScroll={(e) => setScrollY(e.nativeEvent.contentOffset.y)}
          scrollEventThrottle={16}
          onContentSizeChange={(_, h) => setContentHeight(h)}
        >
          {notes.map((note) => (
            <StudentNotesListItem
              key={note.id}
              note={note}
              selected={activeId === note.id}
              onPress={onPressNote}
              onDeletePress={onDeletePress}
              onMenuPress={onMenuPress}
            />
          ))}
        </ScrollView>
      </View>

      <TouchableOpacity
        style={[styles.arrowBtn, !canScroll && styles.arrowDisabled]}
        onPress={() => scrollBy(ROW_HEIGHT)}
        disabled={!canScroll || atBottom}
        accessibilityLabel="Scroll notes down"
      >
        <MaterialIcons name="keyboard-arrow-down" size={28} color={canScroll && !atBottom ? gold : '#666'} />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    alignItems: 'center',
    marginBottom: 8,
  },
  loader: {
    marginTop: 12,
    marginBottom: 12,
  },
  empty: {
    fontSize: 13,
    marginHorizontal: 12,
    marginTop: 8,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  arrowBtn: {
    paddingVertical: 2,
    paddingHorizontal: 24,
    alignItems: 'center',
  },
  arrowDisabled: {
    opacity: 0.35,
  },
  viewport: {
    width: '100%',
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  scroll: {
    flex: 1,
  },
});
